import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';

function splitName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

// Default empty permission shape — MUST match backend keys exactly.
const emptyPerms = () => ({
  documents: { view: false, download: false, delete: false, view_all: false },
  upload: false,
  scheduling: { schedule: false, edit: false, cancel: false },
  manage_users: { add: false, edit: false, delete: false },
  activity: false,
});

// Normalise whatever comes back from the API into the full shape.
function normalisePerms(p) {
  const base = emptyPerms();
  if (!p || typeof p !== 'object') return base;
  return {
    documents: { ...base.documents, ...(p.documents || {}) },
    upload: !!p.upload,
    scheduling: { ...base.scheduling, ...(p.scheduling || {}) },
    manage_users: { ...base.manage_users, ...(p.manage_users || {}) },
    activity: !!p.activity,
  };
}

function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');

  const [roles, setRoles] = useState([]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('');
  const [perms, setPerms] = useState(emptyPerms());
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  // add-new-role inline
  const [addingRole, setAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [roleError, setRoleError] = useState('');

  const [deleteModal, setDeleteModal] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [editModal, setEditModal] = useState(null);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editPerms, setEditPerms] = useState(emptyPerms());
  const [editPassword, setEditPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editError, setEditError] = useState('');

  const isSuperAdmin = user?.role === 'superadmin';

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3500);
  };

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await axios.get(`${API_BASE}/api/users`, { withCredentials: true });
      setUsers(res.data);
    } catch {
      setUsersError('Failed to load users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/roles`, { withCredentials: true });
      setRoles(res.data || []);
    } catch {
      setRoles([]);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers, fetchRoles]);

  const validatePassword = (pwd) => {
    if (!pwd) return 'Required';
    if (pwd.length < 8) return 'Min. 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Need 1 uppercase letter';
    if (!/[0-9]/.test(pwd)) return 'Need 1 number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) return 'Need 1 special character';
    return null;
  };

  const validate = () => {
    const e = {};
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!firstName.trim()) e.firstName = 'Required';
    else if (!nameRegex.test(firstName)) e.firstName = 'Letters only';
    if (!lastName.trim()) e.lastName = 'Required';
    else if (!nameRegex.test(lastName)) e.lastName = 'Letters only';
    if (!email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Invalid email';
    if (!role) e.role = 'Select a role';
    const pwdErr = validatePassword(password);
    if (pwdErr) e.password = pwdErr;
    return e;
  };

  const blockNumbers = (e) => {
    if (/[0-9]/.test(e.key)) e.preventDefault();
  };

  // toggle helpers
  const togglePerm = (setter) => (group, key) => {
    setter(prev => {
      if (key === null) return { ...prev, [group]: !prev[group] }; // single boolean (upload, activity)
      return { ...prev, [group]: { ...prev[group], [key]: !prev[group][key] } };
    });
  };
  const toggleAddPerm = togglePerm(setPerms);
  const toggleEditPerm = togglePerm(setEditPerms);

  const handleAddRole = async () => {
    if (!newRoleName.trim()) { setRoleError('Role name required'); return; }
    try {
      const res = await axios.post(`${API_BASE}/api/roles/add`, {
        name: newRoleName.trim(),
        permissions: {}, // role is just a label; permissions are set per-user
      }, { withCredentials: true });
      setRoles(res.data.roles || []);
      setRole(newRoleName.trim());
      setNewRoleName('');
      setAddingRole(false);
      setRoleError('');
    } catch (err) {
      setRoleError(err.response?.data?.message || 'Failed to add role');
    }
  };

  const handleAdd = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setErrors({});
    try {
      await axios.post(`${API_BASE}/api/users/add`, {
        name: `${firstName.trim()} ${lastName.trim()}`,
        email: email.trim().toLowerCase(),
        password,
        role,
        permissions: perms,
      }, { withCredentials: true });
      showMessage('✓ User added successfully!');
      setFirstName(''); setLastName(''); setEmail('');
      setPassword(''); setRole(''); setPerms(emptyPerms());
      fetchUsers();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to add user';
      if (msg.toLowerCase().includes('email')) {
        setErrors(p => ({ ...p, email: 'This email is already registered' }));
      } else {
        showMessage(msg);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletePassword) { setDeleteError('Please enter your password'); return; }
    try {
      await axios.post(`${API_BASE}/api/users/delete-confirm`, {
        user_id: deleteModal.id,
        sa_password: deletePassword
      }, { withCredentials: true });
      setDeleteModal(null);
      setDeletePassword('');
      setDeleteError('');
      showMessage('✓ User removed successfully');
      fetchUsers();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Incorrect password');
    }
  };

  const handleEditSave = async () => {
    if (!editFirst.trim()) { setEditError('First name is required'); return; }
    if (!editEmail.trim()) { setEditError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmail)) { setEditError('Invalid email'); return; }
    if (editPassword) {
      const pwdErr = validatePassword(editPassword);
      if (pwdErr) { setEditError(pwdErr); return; }
    }
    try {
      const fullName = editLast.trim()
        ? `${editFirst.trim()} ${editLast.trim()}`
        : editFirst.trim();
      await axios.post(`${API_BASE}/api/users/edit`, {
        user_id: editModal.id,
        name: fullName,
        email: editEmail.trim().toLowerCase(),
        role: editRole,
        permissions: editPerms,
        password: editPassword || null
      }, { withCredentials: true });
      setEditModal(null);
      setEditError('');
      showMessage('✓ User updated successfully');
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const inputStyle = (hasError) => ({
    width: '100%', padding: '9px 12px',
    border: `0.5px solid ${hasError ? '#FF766B' : '#AAB0B8'}`,
    borderRadius: '8px', fontSize: '13px', fontFamily: 'Manrope, sans-serif',
    color: '#22272D', background: '#F4FAFF', outline: 'none',
    marginTop: '6px', boxSizing: 'border-box'
  });

  const labelStyle = {
    fontSize: '13px', color: '#22272D', fontWeight: '500', display: 'block'
  };

  const errorText = { fontSize: '11px', color: '#FF766B', marginTop: '3px' };

  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };

  const modalStyle = {
    background: '#fff', borderRadius: '16px', padding: '32px',
    width: '480px', maxWidth: '94vw', border: '0.5px solid #AAB0B8',
    maxHeight: '90vh', overflowY: 'auto'
  };

  const isSuccess = message.startsWith('✓');

  // ── Permission checkbox block (shared by add + edit) ──────────────────
  const PermissionBlock = ({ value, toggle }) => {
    const cb = (checked, onClick, text) => (
      <label style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '13px', color: '#22272D', cursor: 'pointer', marginBottom: '6px'
      }}>
        <input type="checkbox" checked={checked} onChange={onClick} style={{ cursor: 'pointer' }} />
        {text}
      </label>
    );
    const groupTitle = {
      fontSize: '12px', fontWeight: '600', color: '#22272D',
      marginTop: '14px', marginBottom: '8px', textTransform: 'uppercase',
      letterSpacing: '0.5px'
    };
    return (
      <div style={{
        background: '#F4FAFF', border: '0.5px solid #AAB0B8',
        borderRadius: '10px', padding: '14px 16px', marginTop: '8px'
      }}>
        <div style={{ fontSize: '11px', color: '#AAB0B8', marginBottom: '4px' }}>
          Search &amp; filter are available to everyone. Tick the actions this user can perform.
        </div>

        <div style={groupTitle}>Documents</div>
        {cb(value.documents.view, () => toggle('documents', 'view'), 'View documents')}
        {cb(value.documents.download, () => toggle('documents', 'download'), 'Download documents')}
        {cb(value.documents.delete, () => toggle('documents', 'delete'), 'Delete own documents')}
        {cb(value.documents.view_all, () => toggle('documents', 'view_all'), 'View ALL documents (everyone\u2019s uploads)')}

        <div style={groupTitle}>Upload</div>
        {cb(value.upload, () => toggle('upload', null), 'Upload documents')}

        <div style={groupTitle}>Scheduling</div>
        {cb(value.scheduling.schedule, () => toggle('scheduling', 'schedule'), 'Schedule meetings')}
        {cb(value.scheduling.edit, () => toggle('scheduling', 'edit'), 'Edit meetings')}
        {cb(value.scheduling.cancel, () => toggle('scheduling', 'cancel'), 'Cancel meetings')}

        <div style={groupTitle}>Manage Users</div>
        {cb(value.manage_users.add, () => toggle('manage_users', 'add'), 'Add users')}
        {cb(value.manage_users.edit, () => toggle('manage_users', 'edit'), 'Edit users')}
        {cb(value.manage_users.delete, () => toggle('manage_users', 'delete'), 'Delete users')}

        <div style={groupTitle}>Activity Log</div>
        {cb(value.activity, () => toggle('activity', null), 'View activity log')}
      </div>
    );
  };

  return (
    <div>
      <h1 style={{
        fontFamily: 'Sora, sans-serif', fontSize: '22px',
        fontWeight: '600', color: '#22272D', marginBottom: '24px'
      }}>
        Users
      </h1>

      {isSuperAdmin && (
        <div style={{
          background: '#fff', border: '0.5px solid #AAB0B8',
          borderRadius: '12px', padding: '24px', marginBottom: '24px'
        }}>
          <h2 style={{
            fontFamily: 'Sora, sans-serif', fontSize: '16px',
            fontWeight: '600', color: '#22272D', marginBottom: '20px'
          }}>
            Add new user
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>First name</label>
              <input
                autoComplete="off" value={firstName}
                onChange={e => { setFirstName(e.target.value); setErrors(p => ({ ...p, firstName: '' })); }}
                onKeyDown={blockNumbers}
                style={inputStyle(!!errors.firstName)}
              />
              {errors.firstName && <div style={errorText}>{errors.firstName}</div>}
            </div>

            <div>
              <label style={labelStyle}>Last name</label>
              <input
                autoComplete="off" value={lastName}
                onChange={e => { setLastName(e.target.value); setErrors(p => ({ ...p, lastName: '' })); }}
                onKeyDown={blockNumbers}
                style={inputStyle(!!errors.lastName)}
              />
              {errors.lastName && <div style={errorText}>{errors.lastName}</div>}
            </div>

            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email" autoComplete="off" value={email}
                onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })); }}
                style={inputStyle(!!errors.email)}
              />
              {errors.email && <div style={errorText}>{errors.email}</div>}
            </div>

            <div>
              <label style={labelStyle}>Role</label>
              {!addingRole ? (
                <select
                  value={role}
                  onChange={e => {
                    if (e.target.value === '__add__') { setAddingRole(true); setRole(''); }
                    else { setRole(e.target.value); setErrors(p => ({ ...p, role: '' })); }
                  }}
                  style={inputStyle(!!errors.role)}
                >
                  <option value="">Select a role…</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                  <option value="__add__">➕ Add new role…</option>
                </select>
              ) : (
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <input
                    autoComplete="off" value={newRoleName}
                    placeholder="New role name"
                    onChange={e => { setNewRoleName(e.target.value); setRoleError(''); }}
                    style={{ ...inputStyle(!!roleError), marginTop: 0, flex: 1 }}
                  />
                  <button type="button" onClick={handleAddRole} style={{
                    background: '#F1FF5C', color: '#22272D', border: 'none',
                    padding: '0 14px', borderRadius: '8px', fontSize: '13px',
                    fontWeight: '600', cursor: 'pointer'
                  }}>Save</button>
                  <button type="button" onClick={() => { setAddingRole(false); setNewRoleName(''); setRoleError(''); }} style={{
                    background: 'transparent', color: '#22272D', border: '0.5px solid #AAB0B8',
                    padding: '0 12px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer'
                  }}>✕</button>
                </div>
              )}
              {errors.role && <div style={errorText}>{errors.role}</div>}
              {roleError && <div style={errorText}>{roleError}</div>}
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password" value={password}
                  onChange={e => { setPassword(e.target.value); setErrors(p => ({ ...p, password: '' })); }}
                  style={{ ...inputStyle(!!errors.password), paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', color: '#AAB0B8', fontSize: '12px'
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {errors.password
                ? <div style={errorText}>{errors.password}</div>
                : <div style={{ fontSize: '11px', color: '#AAB0B8', marginTop: '4px' }}>
                    Min. 8 chars · 1 uppercase · 1 number · 1 special character
                  </div>
              }
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Permissions</label>
              <PermissionBlock value={perms} toggle={toggleAddPerm} />
            </div>
          </div>

          {message && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
              background: isSuccess ? 'rgba(189,255,192,0.3)' : 'rgba(255,118,107,0.15)',
              color: isSuccess ? '#1a6b1a' : '#a83232', fontSize: '13px'
            }}>
              {message}
            </div>
          )}

          <button type="button" onClick={handleAdd} style={{
            background: '#F1FF5C', color: '#22272D', border: 'none',
            padding: '10px 24px', borderRadius: '8px', fontSize: '14px',
            fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer'
          }}>
            Add User
          </button>
        </div>
      )}

      <div style={{
        background: '#fff', border: '0.5px solid #AAB0B8',
        borderRadius: '12px', padding: '24px'
      }}>
        <h2 style={{
          fontFamily: 'Sora, sans-serif', fontSize: '16px',
          fontWeight: '600', color: '#22272D', marginBottom: '20px'
        }}>
          All users
        </h2>

        {usersLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0' }}>
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%',
              border: '2px solid #F4FAFF', borderTop: '2px solid #F1FF5C',
              animation: 'spin 0.8s linear infinite'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{ fontSize: '13px', color: '#AAB0B8' }}>Loading users...</span>
          </div>
        )}

        {usersError && !usersLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
            <span style={{ fontSize: '13px', color: '#FF766B' }}>{usersError}</span>
            <button type="button" onClick={fetchUsers} style={{
              padding: '6px 16px', borderRadius: '6px', border: 'none',
              background: '#F1FF5C', color: '#22272D', fontSize: '12px',
              fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
            }}>
              Retry
            </button>
          </div>
        )}

        {!usersLoading && !usersError && users.map(u => {
          const isMe = u.id === user.id;
          const isSA = u.role === 'superadmin';

          return (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 0', borderTop: '0.5px solid #F4FAFF'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: '#22272D', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#F1FF5C',
                  fontSize: '13px', fontWeight: '600', flexShrink: 0
                }}>
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#22272D', fontWeight: '500' }}>
                    {u.name} {isMe && <span style={{ fontSize: '11px', color: '#AAB0B8' }}>(you)</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: '#AAB0B8' }}>{u.email}</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '11px', fontWeight: '600', padding: '3px 12px',
                  borderRadius: '20px',
                  background: isSA ? 'rgba(241,255,92,0.25)' : 'rgba(98,240,255,0.2)',
                  color: '#22272D'
                }}>
                  {isSA ? 'Super Admin' : (u.role || '—')}
                </span>

                {(isMe || isSuperAdmin) && (
                  <button type="button" onClick={() => {
                    const { first, last } = splitName(u.name);
                    setEditModal(u);
                    setEditFirst(first);
                    setEditLast(last);
                    setEditEmail(u.email);
                    setEditRole(u.role);
                    setEditPerms(normalisePerms(u.permissions));
                    setEditPassword('');
                    setShowEditPassword(false);
                    setEditError('');
                  }} style={{
                    fontSize: '12px', padding: '5px 12px', borderRadius: '6px',
                    border: '0.5px solid #AAB0B8', background: 'transparent',
                    color: '#22272D', cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
                  }}>
                    Edit
                  </button>
                )}

                {isSuperAdmin && !isMe && !isSA && (
                  <button type="button" onClick={() => {
                    setDeleteModal(u);
                    setDeletePassword('');
                    setDeleteError('');
                  }} style={{
                    fontSize: '12px', padding: '5px 12px', borderRadius: '6px',
                    border: '0.5px solid #FF766B', background: 'transparent',
                    color: '#FF766B', cursor: 'pointer', fontFamily: 'Manrope, sans-serif'
                  }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete modal */}
      {deleteModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{
              fontFamily: 'Sora, sans-serif', fontSize: '18px',
              fontWeight: '600', color: '#22272D', marginBottom: '8px'
            }}>
              Remove user
            </h2>
            <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '20px' }}>
              Are you sure you want to remove{' '}
              <strong style={{ color: '#22272D' }}>{deleteModal.name}</strong>?
              This cannot be undone.
            </p>
            <label style={labelStyle}>Enter your password to confirm</label>
            <input
              type="password" autoComplete="off" value={deletePassword}
              onChange={e => { setDeletePassword(e.target.value); setDeleteError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleDeleteConfirm()}
              style={inputStyle(!!deleteError)}
            />
            {deleteError && <div style={errorText}>{deleteError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button type="button" onClick={handleDeleteConfirm} style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                background: '#FF766B', color: '#fff', fontSize: '14px',
                fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer'
              }}>
                Yes, remove
              </button>
              <button type="button" onClick={() => { setDeleteModal(null); setDeletePassword(''); setDeleteError(''); }} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: '0.5px solid #AAB0B8', background: 'transparent',
                color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif', cursor: 'pointer'
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{
              fontFamily: 'Sora, sans-serif', fontSize: '18px',
              fontWeight: '600', color: '#22272D', marginBottom: '20px'
            }}>
              Edit user
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>First name</label>
                <input
                  autoComplete="off" value={editFirst}
                  onChange={e => setEditFirst(e.target.value)}
                  onKeyDown={blockNumbers}
                  style={inputStyle(false)}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Last name <span style={{ color: '#AAB0B8', fontWeight: '400' }}>(optional)</span>
                </label>
                <input
                  autoComplete="off" value={editLast}
                  onChange={e => setEditLast(e.target.value)}
                  onKeyDown={blockNumbers}
                  style={inputStyle(false)}
                />
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Email</label>
              <input
                autoComplete="off" value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                style={inputStyle(false)}
              />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Role</label>
              {editModal.role === 'superadmin' ? (
                <div style={{
                  ...inputStyle(false), marginTop: '6px', color: '#AAB0B8',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  Super Admin
                  <span style={{ fontSize: '11px', color: '#AAB0B8' }}>· role cannot be changed</span>
                </div>
              ) : (
                <select
                  value={editRole}
                  onChange={e => setEditRole(e.target.value)}
                  style={inputStyle(false)}
                  disabled={!isSuperAdmin}
                >
                  <option value="">Select a role…</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.name}>{r.name}</option>
                  ))}
                </select>
              )}
            </div>

            {editModal.role !== 'superadmin' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Permissions</label>
                <PermissionBlock value={editPerms} toggle={toggleEditPerm} />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                New password{' '}
                <span style={{ color: '#AAB0B8', fontWeight: '400' }}>(leave blank to keep current)</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showEditPassword ? 'text' : 'password'}
                  autoComplete="new-password" value={editPassword}
                  onChange={e => setEditPassword(e.target.value)}
                  style={{ ...inputStyle(false), paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', color: '#AAB0B8', fontSize: '12px'
                  }}
                >
                  {showEditPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {editPassword && (
                <div style={{ fontSize: '11px', color: '#AAB0B8', marginTop: '4px' }}>
                  Min. 8 chars · 1 uppercase · 1 number · 1 special character
                </div>
              )}
            </div>

            {editError && (
              <div style={{ ...errorText, marginBottom: '12px', fontSize: '13px' }}>
                {editError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={handleEditSave} style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                background: '#F1FF5C', color: '#22272D', fontSize: '14px',
                fontWeight: '600', fontFamily: 'Manrope, sans-serif', cursor: 'pointer'
              }}>
                Save changes
              </button>
              <button type="button" onClick={() => { setEditModal(null); setEditError(''); setEditPassword(''); }} style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: '0.5px solid #AAB0B8', background: 'transparent',
                color: '#22272D', fontSize: '14px', fontFamily: 'Manrope, sans-serif', cursor: 'pointer'
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;